import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePartidoInfo } from '../hooks';
import { PartidoInfoCard } from '../components/ui';
import { ClipboardList, Clock, CalendarCheck, FileText, ChevronRight } from 'lucide-react';
import { openExternalUrl } from '@/utils/externalLink';

/**
 * Página principal Home con diseño moderno
 */
export const Home: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { proximoPartido, loading, error } = usePartidoInfo();

  // Accesos rápidos configurados
  const quickActions = [
    {
      title: 'Asistencia',
      subtitle: 'Registrar asistencia',
      icon: ClipboardList,
      path: '/asistencia',
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/20'
    },
    {
      title: 'Minutaje',
      subtitle: 'Control de jornadas',
      icon: Clock,
      path: '/jornadas',
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20'
    },
    {
      title: 'Disponibilidad',
      subtitle: 'Gestionar fechas',
      icon: CalendarCheck,
      path: '/disponibilidad',
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/20'
    },
    {
      title: 'Partidos',
      subtitle: 'Asignados',
      icon: FileText,
      path: '/partidos',
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/20'
    }
  ];

  const handleNavigationWithDelay = (path: string) => {
    // Pequeño retardo para permitir que se vea la animación "active:scale-95" o "active:bg-opacity-30" en móviles
    setTimeout(() => {
      navigate(path);
    }, 200);
  };

  const handleAbrirActaPartido = () => {
    if (!proximoPartido?.urlActa) return;
    openExternalUrl(proximoPartido.urlActa);
  };

  return (
    <div className="flex-1 bg-transparent">
      {/* Header Minimalista */}
      <header className="px-5 pt-6 pb-2 bg-gradient-to-b from-slate-900/90 to-transparent border-b border-slate-800/30">
        <div className="flex justify-between items-end">
          <div>
            <p className="text-slate-400 text-xs font-medium mb-0.5">Bienvenido de nuevo,</p>
            <h1 className="text-xl font-bold text-white tracking-tight">
              {user?.nombre || 'Entrenador'}
            </h1>
          </div>
          {/* Espacio para notificaciones futuras */}
        </div>
      </header>

      <div className="p-4 space-y-6">
        
        {/* Sección Próximo Partido (Hero) */}
        <section className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-lg font-semibold text-slate-100">Próximo Partido</h2>
            {/* Link opcional a calendario completo */}
          </div>

          {loading ? (
            <div className="animate-pulse bg-slate-900 rounded-2xl h-52 border border-slate-800 p-6 flex flex-col justify-center items-center">
              <div className="w-10 h-10 bg-slate-800 rounded-full mb-3"></div>
              <div className="h-4 w-32 bg-slate-800 rounded mb-2"></div>
              <div className="h-3 w-24 bg-slate-800 rounded"></div>
            </div>
          ) : error && !proximoPartido ? (
             <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800 text-center">
                <p className="text-slate-400 text-sm">No se pudo cargar la información del partido.</p>
             </div>
          ) : proximoPartido ? (
            <PartidoInfoCard 
              partidoInfo={proximoPartido}
              onClick={proximoPartido.urlActa ? handleAbrirActaPartido : undefined}
              className="transform transition-all duration-300 hover:scale-[1.02]"
            />
          ) : (
            <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800 text-center flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                <CalendarCheck className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="text-slate-300 font-medium mb-1">Sin partidos próximos</h3>
              <p className="text-slate-500 text-sm">Disfruta del descanso esta semana.</p>
            </div>
          )}
        </section>

        {/* Accesos Rápidos (Grid) */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-100 px-1">Gestión Rápida</h2>
          
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map((action) => (
              <button
                key={action.title}
                onClick={() => handleNavigationWithDelay(action.path)}
                className={`
                  relative overflow-hidden group p-3 rounded-2xl border text-left transition-all duration-200
                  ${action.bg} ${action.border} hover:bg-opacity-20 hover:border-opacity-40 active:scale-[0.97] active:bg-opacity-30
                `}
              >
                <div className="flex justify-between items-start mb-1.5">
                  <div className={`p-1.5 rounded-xl bg-slate-950/30 ${action.color}`}>
                    <action.icon className="w-4 h-4" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1" />
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-200 text-sm mb-0.5 group-hover:text-white transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-[10px] leading-tight text-slate-400 font-medium">
                    {action.subtitle}
                  </p>
                </div>

                {/* Decoración de fondo */}
                <div className={`absolute -bottom-4 -right-4 w-16 h-16 rounded-full opacity-10 blur-2xl ${action.color.replace('text-', 'bg-')}`}></div>
              </button>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
};

export default Home;
